import { useI18n } from "i18n";
import { useCheckEventStatus } from "modules/event/hooks/useCheckEventStatus";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { isEventManager, isEventSpeaker } from "modules/event/utils/eventUtils";
import { IHistoryLocation } from "types/history";
import { IEvent } from "modules/event/types";
import { IUser } from "modules/app/types";
import { getUserName } from "modules/userProfile";
import { sleep } from "helpers/timeHelper";
import logger from "logging/logger";
import { selectUser } from "modules/auth/redux/selectors";
import useEventLiveCapacity from "modules/eventUserPresence/hooks/useEventLiveCapacity";
import useNotificationActions from "modules/notification/hooks/useNotificationActions";
import { usePrevious } from "helpers/reactHooksUtils";
import { hideCameraSettingsDialog } from "modules/cameraSettings/redux/actions";
import { useAppDispatch } from "store/hooks";
import useLobbyGuestFirestore from "./useLobbyGuestFirestore";

const useGuestLobbyPage = (event: IEvent | null) => {
  const history = useHistory<IHistoryLocation | undefined>();
  const { t } = useI18n(["errorPage", "common", "server"]);
  const {
    addGuestInLobby,
    removeGuestFromLobby,
    listenForApproval,
    isGuestApprovedIntoEvent,
  } = useLobbyGuestFirestore();
  const { checkIsAllowedToJoinEventLobby } = useCheckEventStatus();
  const dispatch = useAppDispatch();
  const { addErrorNotification, addSuccessNotification, addInfoNotification } =
    useNotificationActions();
  const user = useSelector(selectUser);
  const { isEventFull } = useEventLiveCapacity(event);
  const [isApproved, setIsApproved] = useState(false);
  const prevIsApproved = usePrevious(isApproved);
  const isApprovedRef = useRef(false);
  const eventRef = useRef<IEvent | null>(null);
  const userRef = useRef<IUser | null>(null);
  const listenerRef = useRef<(() => void) | null>(null);

  isApprovedRef.current = isApproved;
  eventRef.current = event;
  userRef.current = user ?? null;

  const onApproval = () => {
    if (!event) return;

    // guard against undefined
    if (prevIsApproved === false) {
      addSuccessNotification({
        message: t("common:lobby.join.event"),
      });
    }
    setIsApproved(true);
    dispatch(hideCameraSettingsDialog());
  };

  const handleLobbyUnload = () => {
    const userData = userRef.current;

    if (!isApprovedRef.current && userData && eventRef.current) {
      logger.info(
        `[handleLobbyUnload] event: ${eventRef.current.id} isApproved: ${isApprovedRef.current} user: ${userData.id}`,
      );

      if (eventRef.current.id) {
        removeGuestFromLobby(userData.id, eventRef.current.id);
      }
      sleep(500);
    }
  };

  const checkGuestStatus = async () => {
    if (!event || !event.isLobbyActive) {
      return;
    }

    logger.info(`[LobbyPage] isFull: ${isEventFull} event: ${event.id}`);

    if (isEventFull) {
      addInfoNotification({
        message: t("server:addon.guest.limit.overdue.exceed"),
      });
      history.push(`/e/${event.code}/register`);

      return;
    }

    if (event.id && user?.id) {
      const approved = await isGuestApprovedIntoEvent(event.id, user.id);

      if (!approved) {
        addGuestInLobby(user.id, getUserName(user), event.id);
        if (listenerRef.current) {
          listenerRef.current();
        }
        listenerRef.current = listenForApproval(user.id, event.id, onApproval);
      } else {
        onApproval();
      }
    }
  };

  useEffect(() => {
    // Track when user leaving lobby page
    window.addEventListener("beforeunload", handleLobbyUnload);

    return () => {
      window.removeEventListener("beforeunload", handleLobbyUnload);
      // On component unmount, only if pending.
      handleLobbyUnload();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!event?.id || !user) {
      return;
    }

    if (
      isEventManager(event, user.id) ||
      isEventSpeaker(event, user.id, user.email)
    ) {
      setIsApproved(true);

      return;
    }

    if (!checkIsAllowedToJoinEventLobby(event)) {
      addErrorNotification({
        message: t("errorPage:event.inactive"),
      });
      history.push(`/e/${event.code}/register`);
      removeGuestFromLobby(user.id, event.id);

      return;
    }

    checkGuestStatus();

    // eslint-disable-next-line consistent-return
    return () => {
      if (listenerRef.current) {
        logger.info(
          `[Lobbypage] unsubscribing user: ${user.id} event: ${event.id}`,
        );
        listenerRef.current();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, user, isEventFull]);

  return { isApproved };
};

export default useGuestLobbyPage;
